/**
 * Specification Editor Component
 *
 * Full-featured TipTap editor for creating and editing specifications.
 * Part of PowerSpec V3 - Spec-Driven Development
 *
 * Features:
 * - TipTap rich text editing with toolbar
 * - Auto-save with debounce
 * - Template structure awareness
 * - Tag management
 * - Status transitions
 * - Embellishment integration
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Card, Button, TextInput, Badge, Spinner, Alert, Modal, Dropdown } from 'flowbite-react';
import {
  HiPencilAlt,
  HiCode,
  HiOutlineViewList,
  HiSave,
  HiX,
  HiTag,
  HiPlus,
  HiSparkles,
  HiCheckCircle,
  HiExclamationCircle,
  HiClock,
  HiDocumentText,
  HiChevronDown
} from 'react-icons/hi';
import {
  updateSpecification,
  embellishSpecification,
  createSpecificationVersion,
  type Specification,
  type UpdateSpecificationRequest,
  type EmbellishmentRequest
} from '../api/specifications';
import { useS3Content } from '../hooks/useS3Content';

interface SpecEditorProps {
  spec: Specification;
  onSave?: (spec: Specification) => void;
  onCancel?: () => void;
  onStatusChange?: (newStatus: Specification['status']) => void;
  autoSave?: boolean;
  autoSaveDelay?: number;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const SpecEditor: React.FC<SpecEditorProps> = ({
  spec,
  onSave,
  onCancel,
  onStatusChange,
  autoSave = true,
  autoSaveDelay = 2000
}) => {
  const [title, setTitle] = useState(spec.title);
  const [tags, setTags] = useState<string[]>(spec.tags || []);
  const [newTag, setNewTag] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showEmbellishModal, setShowEmbellishModal] = useState(false);
  const [embellishing, setEmbellishing] = useState(false);
  const [embellishmentResults, setEmbellishmentResults] = useState<any[]>([]);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionSummary, setVersionSummary] = useState('');
  const [creatingVersion, setCreatingVersion] = useState(false);

  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedContentRef = useRef<string | null>(null);

  // S3 Content Storage - load/save content via presigned URLs
  const {
    content: s3Content,
    loading: s3Loading,
    saving: s3Saving,
    saveContent: saveToS3,
    needsMigration,
    error: s3Error
  } = useS3Content({
    type: 'specification',
    id: spec.id,
    onError: (err) => setError(err),
    autoLoad: true
  });

  // Determine initial content: S3 content > legacy DB content > empty
  const initialContent = s3Content || (needsMigration ? spec.content : null) || spec.content || spec.content_markdown || '';

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4]
        }
      })
    ],
    content: initialContent,
    editable: true,
    onUpdate: ({ editor }) => {
      setHasUnsavedChanges(true);

      if (autoSave) {
        // Clear existing timer
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }

        // Set new timer for auto-save
        autoSaveTimerRef.current = setTimeout(() => {
          handleSave(false);
        }, autoSaveDelay);
      }
    }
  }, [s3Content]); // Re-initialize when S3 content loads

  // Store initial content for comparison
  useEffect(() => {
    if (editor && spec.content) {
      lastSavedContentRef.current = JSON.stringify(spec.content);
    }
  }, [editor, spec.content]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = useCallback(async (showFeedback = true) => {
    if (!editor) return;

    const currentContent = editor.getJSON();
    const currentContentStr = JSON.stringify(currentContent);

    // Skip if content hasn't changed
    if (currentContentStr === lastSavedContentRef.current && title === spec.title) {
      if (showFeedback) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
      return;
    }

    try {
      setSaveStatus('saving');
      setError(null);

      // Save content to S3 first (if available)
      const s3SaveSuccess = await saveToS3(currentContent);
      if (!s3SaveSuccess) {
        console.warn('[SpecEditor] S3 save failed, falling back to database only');
      }

      // Always save metadata and markdown to database
      const updateData: UpdateSpecificationRequest = {
        title,
        content: s3SaveSuccess ? undefined : currentContent, // Only save to DB if S3 failed
        content_markdown: editor.getText(),
        tags
      };

      const response = await updateSpecification(spec.id, updateData);

      if (response.success && response.data?.Records?.[0]) {
        // The API returns APIResponse<{ Records: Specification[] }>
        // So response.data.Records[0] contains { Records: Specification[] }
        const dataWrapper = response.data.Records[0] as unknown as { Records: Specification[] };
        if (dataWrapper.Records?.[0]) {
          lastSavedContentRef.current = currentContentStr;
          setHasUnsavedChanges(false);
          setSaveStatus('saved');

          if (showFeedback) {
            setTimeout(() => setSaveStatus('idle'), 2000);
          }

          onSave?.(dataWrapper.Records[0]);
        } else {
          throw new Error('Failed to save specification');
        }
      } else {
        throw new Error(response.message || 'Failed to save specification');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save';
      setError(errorMessage);
      setSaveStatus('error');
      console.error('[SpecEditor] Save error:', err);
    }
  }, [editor, title, tags, spec.id, spec.title, onSave, saveToS3]);

  const handleSaveWithVersionCheck = useCallback(() => {
    // If spec already has version > 1, prompt for version snapshot before saving
    if (spec.version > 1 && hasUnsavedChanges) {
      setShowVersionModal(true);
    } else {
      handleSave(true);
    }
  }, [spec.version, hasUnsavedChanges, handleSave]);

  const handleCreateVersionAndSave = useCallback(async () => {
    if (!spec.client_id) {
      // Fall back to save without version snapshot if no client_id
      handleSave(true);
      setShowVersionModal(false);
      return;
    }

    try {
      setCreatingVersion(true);
      setError(null);

      // Create version snapshot of current state before saving
      await createSpecificationVersion(spec.id, {
        Client_ID: spec.client_id,
        change_type: 'update',
        change_summary: versionSummary || undefined
      });

      console.log('[SpecEditor] Version snapshot created for spec', spec.id);
    } catch (err) {
      console.error('[SpecEditor] Version snapshot error (proceeding with save):', err);
      // Non-blocking: proceed with save even if version snapshot fails
    } finally {
      setCreatingVersion(false);
      setShowVersionModal(false);
      setVersionSummary('');
    }

    // Proceed with the actual save
    handleSave(true);
  }, [spec.id, spec.client_id, versionSummary, handleSave]);

  const handleSkipVersionAndSave = useCallback(() => {
    setShowVersionModal(false);
    setVersionSummary('');
    handleSave(true);
  }, [handleSave]);

  const handleAddTag = () => {
    const trimmedTag = newTag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setNewTag('');
      setHasUnsavedChanges(true);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
    setHasUnsavedChanges(true);
  };

  const handleStatusChange = async (newStatus: Specification['status']) => {
    try {
      setSaveStatus('saving');

      const response = await updateSpecification(spec.id, { status: newStatus });

      if (response.success) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
        onStatusChange?.(newStatus);
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      setError(errorMessage);
      setSaveStatus('error');
    }
  };

  const handleEmbellish = async (types: EmbellishmentRequest['embellishment_types']) => {
    try {
      setEmbellishing(true);
      setError(null);

      const response = await embellishSpecification(spec.id, {
        embellishment_types: types,
        context: spec.connected_repo ? { connected_repo: spec.connected_repo } : undefined
      });

      if (response.success && response.data?.Records?.[0]) {
        // The API returns APIResponse with nested structure
        const dataWrapper = response.data.Records[0] as unknown as {
          spec_id: string;
          embellishments: {
            type: string;
            content: string;
            confidence_score: number;
          }[];
        };
        setEmbellishmentResults(dataWrapper.embellishments || []);
      } else {
        throw new Error(response.message || 'Failed to embellish specification');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to embellish';
      setError(errorMessage);
    } finally {
      setEmbellishing(false);
    }
  };

  const applyEmbellishment = (content: string) => {
    if (editor) {
      // Append embellishment content at cursor or end
      editor.chain().focus().insertContent(`\n\n${content}`).run();
      setHasUnsavedChanges(true);
    }
    setShowEmbellishModal(false);
    setEmbellishmentResults([]);
  };

  const getSaveStatusIcon = () => {
    // Show saving state if either DB or S3 is saving
    if (saveStatus === 'saving' || s3Saving) {
      return <Spinner size="sm" />;
    }
    switch (saveStatus) {
      case 'saved':
        return <HiCheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <HiExclamationCircle className="w-5 h-5 text-red-500" />;
      default:
        return hasUnsavedChanges ? <HiClock className="w-5 h-5 text-yellow-500" /> : null;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'draft': return 'gray';
      case 'in_review': return 'warning';
      case 'changes_requested': return 'failure';
      case 'approved': return 'success';
      case 'published': return 'info';
      case 'archived': return 'dark';
      default: return 'gray';
    }
  };

  // Show loading state for editor or S3 content
  if (!editor || s3Loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Spinner size="xl" />
        {s3Loading && <p className="mt-2 text-sm text-gray-500">Loading content...</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <TextInput
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setHasUnsavedChanges(true);
              }}
              placeholder="Specification title"
              className="text-xl font-bold"
              sizing="lg"
            />
            <div className="flex items-center gap-2 mt-2">
              <Badge color={getStatusBadgeColor(spec.status)}>
                {spec.status.replace('_', ' ').toUpperCase()}
              </Badge>
              <span className="text-sm text-gray-500">v{spec.version}</span>
              <span className="text-xs text-gray-400 font-mono">{spec.business_id}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {getSaveStatusIcon()}

            <Button
              size="sm"
              color="gray"
              onClick={handleSaveWithVersionCheck}
              disabled={saveStatus === 'saving'}
            >
              <HiSave className="w-4 h-4 mr-1" />
              Save
            </Button>

            <Dropdown
              label=""
              dismissOnClick={true}
              renderTrigger={() => (
                <Button size="sm" color="light">
                  Status <HiChevronDown className="w-4 h-4 ml-1" />
                </Button>
              )}
            >
              <Dropdown.Item onClick={() => handleStatusChange('draft')}>
                Draft
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleStatusChange('in_review')}>
                Submit for Review
              </Dropdown.Item>
              {spec.status === 'approved' && (
                <Dropdown.Item onClick={() => handleStatusChange('published')}>
                  Publish
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => handleStatusChange('archived')}>
                Archive
              </Dropdown.Item>
            </Dropdown>

            <Button
              size="sm"
              color="purple"
              onClick={() => setShowEmbellishModal(true)}
            >
              <HiSparkles className="w-4 h-4 mr-1" />
              AI Enhance
            </Button>

            {onCancel && (
              <Button size="sm" color="light" onClick={onCancel}>
                <HiX className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Editor Toolbar */}
      <Card>
        <div className="flex flex-wrap items-center gap-1 pb-3 border-b border-gray-200 mb-3">
          {/* Text Formatting */}
          <Button
            size="xs"
            color={editor.isActive('bold') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <HiPencilAlt className="w-4 h-4" />
          </Button>
          <Button
            size="xs"
            color={editor.isActive('italic') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <span className="italic font-serif">I</span>
          </Button>
          <Button
            size="xs"
            color={editor.isActive('strike') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <span className="line-through">S</span>
          </Button>
          <Button
            size="xs"
            color={editor.isActive('code') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <HiCode className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-gray-300 mx-2" />

          {/* Headings */}
          <Button
            size="xs"
            color={editor.isActive('heading', { level: 1 }) ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            H1
          </Button>
          <Button
            size="xs"
            color={editor.isActive('heading', { level: 2 }) ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </Button>
          <Button
            size="xs"
            color={editor.isActive('heading', { level: 3 }) ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </Button>

          <div className="w-px h-6 bg-gray-300 mx-2" />

          {/* Lists */}
          <Button
            size="xs"
            color={editor.isActive('bulletList') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <HiOutlineViewList className="w-4 h-4" />
          </Button>
          <Button
            size="xs"
            color={editor.isActive('orderedList') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            1.
          </Button>

          <div className="w-px h-6 bg-gray-300 mx-2" />

          {/* Block Elements */}
          <Button
            size="xs"
            color={editor.isActive('codeBlock') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            {'</>'}
          </Button>
          <Button
            size="xs"
            color={editor.isActive('blockquote') ? 'blue' : 'light'}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            "
          </Button>
          <Button
            size="xs"
            color="light"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            —
          </Button>
        </div>

        {/* Editor Content */}
        <div className="prose max-w-none min-h-[400px] focus-within:ring-2 focus-within:ring-blue-500 rounded p-2">
          <EditorContent editor={editor} />
        </div>
      </Card>

      {/* Tags Section */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <HiTag className="w-5 h-5 text-gray-400" />
          <span className="font-medium">Tags</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {tags.map((tag) => (
            <Badge
              key={tag}
              color="blue"
              className="cursor-pointer hover:bg-blue-200"
              onClick={() => handleRemoveTag(tag)}
            >
              {tag}
              <HiX className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>

        <div className="flex gap-2">
          <TextInput
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
            placeholder="Add a tag..."
            sizing="sm"
            className="flex-1"
          />
          <Button size="sm" color="light" onClick={handleAddTag}>
            <HiPlus className="w-4 h-4" />
          </Button>
        </div>
      </Card>

      {/* Template Info (if available) */}
      {spec.template_name && (
        <Card>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <HiDocumentText className="w-5 h-5" />
            <span>Template: <strong>{spec.template_name}</strong></span>
            {spec.template_category && (
              <Badge color="gray" size="sm">{spec.template_category}</Badge>
            )}
          </div>
        </Card>
      )}

      {/* Version Snapshot Modal */}
      <Modal show={showVersionModal} onClose={() => setShowVersionModal(false)} size="md">
        <Modal.Header>
          <div className="flex items-center gap-2">
            <HiClock className="w-5 h-5 text-blue-500" />
            Create Version Snapshot
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This specification is at version {spec.version}. Would you like to create a
              snapshot of the current state before saving your changes?
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Change Summary (optional)
              </label>
              <TextInput
                value={versionSummary}
                onChange={(e) => setVersionSummary(e.target.value)}
                placeholder="Describe what changed..."
                sizing="sm"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex gap-2 w-full justify-end">
            <Button
              color="gray"
              onClick={handleSkipVersionAndSave}
              disabled={creatingVersion}
            >
              Skip & Save
            </Button>
            <Button
              color="blue"
              onClick={handleCreateVersionAndSave}
              disabled={creatingVersion}
            >
              {creatingVersion ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Snapshot & Save'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* AI Embellishment Modal */}
      <Modal show={showEmbellishModal} onClose={() => setShowEmbellishModal(false)} size="lg">
        <Modal.Header>
          <div className="flex items-center gap-2">
            <HiSparkles className="w-5 h-5 text-purple-500" />
            AI Specification Enhancement
          </div>
        </Modal.Header>
        <Modal.Body>
          {embellishing ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Spinner size="xl" />
              <p className="mt-4 text-gray-600">Generating enhancements...</p>
            </div>
          ) : embellishmentResults.length > 0 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select an enhancement to add to your specification:
              </p>
              {embellishmentResults.map((result, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge color="purple">{result.type}</Badge>
                    <span className="text-xs text-gray-500">
                      Confidence: {Math.round(result.confidence_score * 100)}%
                    </span>
                  </div>
                  <div className="prose prose-sm max-w-none mb-3 max-h-40 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm bg-gray-50 p-3 rounded">
                      {result.content}
                    </pre>
                  </div>
                  <Button
                    size="sm"
                    color="purple"
                    onClick={() => applyEmbellishment(result.content)}
                  >
                    Apply to Spec
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Choose which AI enhancements to generate for your specification:
              </p>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  color="light"
                  onClick={() => handleEmbellish(['technical_depth'])}
                >
                  Technical Details
                </Button>
                <Button
                  color="light"
                  onClick={() => handleEmbellish(['risk_analysis'])}
                >
                  Risk Analysis
                </Button>
                <Button
                  color="light"
                  onClick={() => handleEmbellish(['acceptance_criteria'])}
                >
                  Acceptance Criteria
                </Button>
                <Button
                  color="light"
                  onClick={() => handleEmbellish(['test_cases'])}
                >
                  Test Cases
                </Button>
                <Button
                  color="light"
                  onClick={() => handleEmbellish(['dependency_suggestions'])}
                >
                  Dependencies
                </Button>
                <Button
                  color="purple"
                  onClick={() => handleEmbellish([
                    'technical_depth',
                    'risk_analysis',
                    'acceptance_criteria',
                    'test_cases'
                  ])}
                >
                  <HiSparkles className="w-4 h-4 mr-1" />
                  Full Enhancement
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => {
            setShowEmbellishModal(false);
            setEmbellishmentResults([]);
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SpecEditor;
